import { useAppDispatch, useAppSelector } from '@app/store';
import { selectFileDownloadUrl } from '@app/store/selectors';
import { useEffect } from 'react';

// Refresh the presigned URL slightly before it expires so the link stays
// valid for as long as the banner remains mounted.
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;

export const useDownloadUrl = (
  filename: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const dispatch = useAppDispatch();
  const fileDownloadUrl = useAppSelector(selectFileDownloadUrl(filename));

  const isExpired = Boolean(
    fileDownloadUrl?.expiresAt && Date.now() >= fileDownloadUrl.expiresAt,
  );
  const needsFetch = !fileDownloadUrl || isExpired;

  useEffect(() => {
    if (!enabled || !needsFetch) return;

    dispatch({
      type: 'download-url - fetch requested',
      payload: { filename },
    });
  }, [enabled, needsFetch, filename, dispatch]);

  useEffect(() => {
    if (!enabled || !fileDownloadUrl?.expiresAt || isExpired) return;

    const msUntilRefresh = Math.max(
      fileDownloadUrl.expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS,
      0,
    );

    const timeoutId = setTimeout(() => {
      dispatch({
        type: 'download-url - fetch requested',
        payload: { filename },
      });
    }, msUntilRefresh);

    return () => clearTimeout(timeoutId);
  }, [enabled, fileDownloadUrl?.expiresAt, isExpired, filename, dispatch]);

  return {
    url: isExpired ? null : fileDownloadUrl?.url ?? null,
    isLoading: fileDownloadUrl?.loading ?? false,
    error: fileDownloadUrl?.error ?? null,
  };
};
